import TextField, { TextFieldProps } from "@mui/material/TextField";
import React, { useEffect, useState } from "react";
import { useGlobalSettings } from "../../context/GlobalSettingsContext";

export function formatCurrencyDisplay(
  value: number | string,
  currency: string,
  decimalPlaces: number = 0,
): string {
  let num =
    typeof value === "string"
      ? parseFloat(value.replace(/[^\d.]/g, ""))
      : value;
  if (isNaN(num)) return "";
  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: currency,
    minimumFractionDigits: decimalPlaces,
    maximumFractionDigits: decimalPlaces,
  }).format(num);
}

function transformCurrency(
  message: string,
  currency: string,
  decimalPlaces: number = 0,
): string {
  return message.replace(/\[CUR\](.*?)\[\/CUR\]/g, (_, numStr) => {
    return formatCurrencyDisplay(numStr, currency, decimalPlaces);
  });
}

export const CurrencyDisplay: React.FC<{
  message: string;
  decimalPlaces?: number;
}> = ({ message, decimalPlaces = 0 }) => {
  const { settings } = useGlobalSettings();
  return (
    <span>{transformCurrency(message, settings.currency, decimalPlaces)}</span>
  );
};

type CurrencyFieldProps = Omit<TextFieldProps, "value" | "onChange"> & {
  value: number | undefined;
  onChange: (value: number) => void;
  showZero?: boolean;
  placeholderValue?: number;
  greaterThanZero?: boolean;
};

export const CurrencyField: React.FC<CurrencyFieldProps> = ({
  value,
  onChange,
  label,
  showZero = false,
  placeholderValue = undefined,
  greaterThanZero = false,
  ...props
}) => {
  const { settings } = useGlobalSettings();
  const [displayValue, setDisplayValue] = useState("");

  useEffect(() => {
    if (value !== undefined && (showZero || value !== 0)) {
      setDisplayValue(formatCurrencyDisplay(value, settings.currency));
    } else {
      setDisplayValue("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, settings.currency]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;

    let cleaned = raw.replace(/[^\d.]/g, "");
    const parts = cleaned.split(".");

    if (parts.length > 2) {
      cleaned = parts[0] + "." + parts.slice(1).join("");
    }

    if (parts.length === 2) {
      parts[1] = parts[1].slice(0, 2);
      cleaned = parts[0] + "." + parts[1];
    }

    const num = parseFloat(cleaned);
    onChange(!isNaN(num) ? num : 0);

    setDisplayValue(formatCurrencyDisplay(cleaned, settings.currency));
  };

  return (
    <TextField
      {...props}
      type="text"
      label={label ?? `Amount (${settings.currency})`}
      value={displayValue}
      onChange={handleChange}
      placeholder={
        placeholderValue !== undefined
          ? formatCurrencyDisplay(placeholderValue, settings.currency)
          : undefined
      }
      inputRef={(ref) => {
        if (greaterThanZero && ref) {
          ref.setCustomValidity(
            value !== undefined && value <= 0 ? "Must be greater than 0" : "",
          );
        }
      }}
    />
  );
};
